import { Link } from 'react-router-dom';
import { ChevronRight, History, LogOut, Menu, UserRound, X } from 'lucide-react';
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { SidebarCount } from '@/components/ui/sidebar-count';
import { groupNavItems, isNavItemActive } from '@/lib/nav';
import { formatNumber } from '@/lib/format';
import { userInitials } from '@/components/entry/user-initials';
import { cn } from '@/lib/utils';

/*
  App shell navigation (02 #8): the grouped navy sidebar, the phone bottom tab bar and its "More" sheet.
  Items come from Layout's NAV_ITEMS (labels, routes, icons, permissions — already filtered for the user);
  lib/nav groups them: Workspace (no heading) / Delivery / Supply / Finance / Tools.
  `counts` = GET /dashboard/nav-counts → { '/projects': { count, tone, label } }.
*/

const navTestId = (prefix, item) => `${prefix}-${item.label.toLowerCase()}`;

function SidebarLink({ item, active, collapsed, count, onNavigate }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.path}
      data-testid={navTestId('nav', item)}
      data-active={active ? 'true' : 'false'}
      aria-current={active ? 'page' : undefined}
      title={collapsed ? [item.label, count?.label].filter(Boolean).join(' · ') : undefined}
      onClick={onNavigate}
      className={cn(
        'ui-nav-item py-[9px] data-[active=true]:text-white',
        collapsed && 'justify-center px-0'
      )}
    >
      {active && !collapsed ? (
        <span className="absolute -left-3 bottom-2 top-2 w-[3px] rounded-r-[3px] bg-lime" aria-hidden="true" />
      ) : null}
      <Icon className={cn('h-[18px] w-[18px] shrink-0', active && 'text-lime')} strokeWidth={1.75} aria-hidden="true" />
      {!collapsed ? <span className="min-w-0 truncate">{item.label}</span> : null}
      <SidebarCount
        count={count?.count}
        tone={count?.tone === 'alert' ? 'alert' : 'default'}
        label={count?.label}
        max={999}
        collapsed={collapsed}
      />
    </Link>
  );
}

/** The grouped sidebar list. Collapsed (icons only): group headings become thin rules. */
export function SidebarNav({ items, pathname, collapsed = false, counts = {}, onNavigate }) {
  const groups = groupNavItems(items);
  return (
    <nav className="flex flex-col px-3 py-2" aria-label="Primary" data-testid="sidebar-nav">
      {groups.map((group, index) => (
        <div key={group.key} role="group" aria-label={group.label} className="flex flex-col gap-0.5" data-testid={`nav-group-${group.key}`}>
          {group.showLabel ? (
            collapsed
              ? <div className={cn('mx-2 h-px bg-white/10', index ? 'my-2.5' : 'mb-2.5')} aria-hidden="true" />
              : <div className="ui-sb-section">{group.label}</div>
          ) : null}
          {group.items.map((item) => (
            <SidebarLink
              key={item.path}
              item={item}
              active={isNavItemActive(item.path, pathname)}
              collapsed={collapsed}
              count={counts[item.path]}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      ))}
    </nav>
  );
}

const PHONE_TAB_PATHS = ['/', '/projects', '/tasks', '/inventory'];
const PHONE_TAB_LABELS = { '/': 'Home', '/inventory': 'Stock' };
const PHONE_TABS = 4;

/** The 4 tabs a phone shows (Home, Projects, Tasks, Stock), topped up from the rest when a role lacks one. */
export function pickPhoneTabs(items) {
  const preferred = PHONE_TAB_PATHS.map((path) => items.find((item) => item.path === path)).filter(Boolean);
  const rest = items.filter((item) => !preferred.includes(item));
  return [...preferred, ...rest].slice(0, PHONE_TABS).map((item) => ({ ...item, tabLabel: PHONE_TAB_LABELS[item.path] || item.label }));
}

/** Phone bottom tab bar (below md): Home · Projects · Tasks · Stock · More. */
export function PhoneTabBar({ items, pathname, counts = {}, onMore, moreOpen }) {
  const tabs = pickPhoneTabs(items);
  const onATab = tabs.some((tab) => isNavItemActive(tab.path, pathname));
  const cell = 'relative flex min-w-0 flex-col items-center justify-center gap-[3px] text-[11px] font-semibold leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring';
  return (
    <nav
      aria-label="Main"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface pb-[env(safe-area-inset-bottom)] shadow-[0_-1px_12px_rgb(0_0_0/0.04)] md:hidden"
      data-testid="phone-tabbar"
    >
      <div className="grid h-16" style={{ gridTemplateColumns: `repeat(${tabs.length + 1}, minmax(0, 1fr))` }}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = isNavItemActive(tab.path, pathname);
          const count = counts[tab.path];
          const alert = count?.tone === 'alert' && Number(count.count) > 0;
          return (
            <Link
              key={tab.path}
              to={tab.path}
              aria-current={active ? 'page' : undefined}
              data-testid={`tab-nav-${tab.tabLabel.toLowerCase().replace(/\s+/g, '-')}`}
              className={cn(cell, active ? 'text-brand-ink' : 'text-ink-3 hover:text-ink-2')}
            >
              <span className={cn('relative grid h-7 w-12 place-items-center rounded-full transition-colors', active && 'bg-brand-soft')}>
                <Icon className="h-[21px] w-[21px]" strokeWidth={active ? 2 : 1.75} aria-hidden="true" />
                {alert ? (
                  <span aria-hidden="true" className="absolute -top-1 right-0.5 min-w-[18px] rounded-full bg-lime px-1 py-[2px] text-center text-[10px] font-bold leading-none text-navy-950">
                    {Number(count.count) > 99 ? '99+' : formatNumber(count.count, { maxDecimals: 0 })}
                  </span>
                ) : null}
              </span>
              <span className="max-w-full truncate px-1" data-tab-label="">{tab.tabLabel}</span>
              {alert && count.label ? <span className="sr-only">, {count.label}</span> : null}
            </Link>
          );
        })}
        <button
          type="button"
          onClick={onMore}
          aria-haspopup="dialog"
          aria-expanded={moreOpen ? 'true' : 'false'}
          data-testid="mobile-nav-toggle"
          className={cn(cell, !onATab || moreOpen ? 'text-brand-ink' : 'text-ink-3 hover:text-ink-2')}
        >
          <span className={cn('grid h-7 w-12 place-items-center rounded-full', (!onATab || moreOpen) && 'bg-brand-soft')}>
            <Menu className="h-[21px] w-[21px]" strokeWidth={1.75} aria-hidden="true" />
          </span>
          More
        </button>
      </div>
    </nav>
  );
}

function SheetRow({ icon: Icon, label, onClick, to, active, count, testId, tone }) {
  const body = (
    <>
      <span className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-[9px]', active ? 'bg-brand text-brand-fg' : 'bg-surface-2 text-ink-2')}>
        <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden="true" />
      </span>
      <span className={cn('min-w-0 flex-1 truncate text-[14.5px]', active ? 'font-bold text-ink' : 'font-semibold text-ink', tone === 'red' && 'text-status-red-ink')}>
        {label}
      </span>
      {count && Number(count.count) > 0 ? (
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-[12px] font-bold tabular-nums',
            count.tone === 'alert' ? 'bg-lime text-navy-950' : 'bg-surface-2 text-ink-2'
          )}
        >
          <span aria-hidden={count.label ? 'true' : undefined}>{formatNumber(count.count, { maxDecimals: 0 })}</span>
          {count.label ? <span className="sr-only">{count.label}</span> : null}
        </span>
      ) : null}
      {to ? <ChevronRight className="h-4 w-4 shrink-0 text-ink-4" aria-hidden="true" /> : null}
    </>
  );
  const classes = 'flex min-h-[46px] w-full items-center gap-3 rounded-control px-2 text-left hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
  if (to) {
    return (
      <Link to={to} onClick={onClick} className={classes} aria-current={active ? 'page' : undefined} data-testid={testId}>
        {body}
      </Link>
    );
  }
  return <button type="button" onClick={onClick} className={classes} data-testid={testId}>{body}</button>;
}

/** Phone "More": every page in its group, then the account (Profile, Recent changes, Sign out). */
export function MoreSheet({ open, onOpenChange, items, pathname, counts = {}, user, account }) {
  const groups = groupNavItems(items);
  const close = () => onOpenChange(false);
  const run = (action) => () => {
    close();
    // let the sheet close before a dialog opens (focus returns cleanly)
    setTimeout(action, 0);
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        hideClose
        className="max-h-[88dvh] overflow-y-auto rounded-t-[20px] border-line bg-surface p-0 pb-[calc(12px+env(safe-area-inset-bottom))] text-ink md:hidden"
        data-testid="more-sheet"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-line-2 bg-surface px-4 py-3">
          <div>
            <SheetTitle className="font-heading text-[17px] font-bold text-ink">Menu</SheetTitle>
            <SheetDescription className="sr-only">All pages and your account</SheetDescription>
          </div>
          <SheetClose className="grid h-11 w-11 place-items-center rounded-full text-ink-3 hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <X className="h-5 w-5" aria-hidden="true" />
            <span className="sr-only">Close menu</span>
          </SheetClose>
        </div>
        <nav aria-label="All pages" className="px-2 pt-1">
          {groups.map((group) => (
            <div key={group.key} role="group" aria-label={group.label}>
              <p className="px-2 pb-1 pt-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-3">{group.label}</p>
              {group.items.map((item) => (
                <SheetRow
                  key={item.path}
                  icon={item.icon}
                  label={item.label}
                  to={item.path}
                  onClick={close}
                  active={isNavItemActive(item.path, pathname)}
                  count={counts[item.path]}
                  testId={navTestId('more-nav', item)}
                />
              ))}
            </div>
          ))}
        </nav>
        {user ? (
          <div className="mx-2 mt-3 border-t border-line-2 pt-2" data-testid="more-account">
            <div className="flex items-center gap-3 px-2 py-2">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-navy text-[12px] font-bold text-white" aria-hidden="true">
                {userInitials(user.name)}
              </span>
              <div className="min-w-0">
                <p className="truncate text-[14px] font-bold text-ink">{user.name}</p>
                <p className="truncate text-[12.5px] text-ink-3">{user.role}</p>
              </div>
            </div>
            <SheetRow icon={UserRound} label="Profile" onClick={run(account.openProfile)} testId="more-profile" />
            <SheetRow icon={History} label="Recent changes · Undo" onClick={run(account.openRecent)} testId="more-recent-changes" />
            <SheetRow icon={LogOut} label="Sign out" onClick={run(account.signOut)} testId="more-sign-out" />
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
